import { GridDrawer } from './GridDrawer';
import { DataManager } from './DataManager';
import { CalendarInfoModal } from './CalendarInfoModal';
import {escapeHtml, showError, showWarning} from "./utils.ts";
import dayjs from 'dayjs';
import { EventPopup } from './EventPopup';
import { AccountModal } from './AccountModal';

const DEFAULT_EVENT_COLOR = '#2db5d5';

export class CalendarPage {
  private readonly pageEl: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly canvasContainer: HTMLElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly tooltip: HTMLElement;
  private readonly slidingPanel: HTMLElement;
  private readonly panelToggle: HTMLButtonElement;
  private readonly accountBtn: HTMLButtonElement;
  private readonly calendarList: HTMLElement;
  private readonly addCalendarBtn: HTMLButtonElement;
  private readonly dataManager: DataManager;
  private readonly grid: GridDrawer;
  private readonly calendarInfoModal: CalendarInfoModal;
  private readonly eventPopup: EventPopup;
  private readonly accountModal: AccountModal;

  constructor(dataManager: DataManager) {
    this.pageEl = document.getElementById('calendarPage') as HTMLElement;
    this.canvas = document.getElementById('lifeCanvas') as HTMLCanvasElement;
    this.canvasContainer = document.getElementById('canvasContainer') as HTMLElement;
    this.ctx = this.canvas.getContext('2d')!;
    this.tooltip = document.getElementById('tooltip') as HTMLElement;
    this.slidingPanel = document.getElementById('slidingPanel') as HTMLElement;
    this.panelToggle = document.getElementById('panelToggle') as HTMLButtonElement;
    this.accountBtn = document.getElementById('accountBtn') as HTMLButtonElement;
    this.calendarList = document.getElementById('calendarList') as HTMLElement;
    this.addCalendarBtn = document.getElementById('addCalendarBtn') as HTMLButtonElement;

    this.dataManager = dataManager;
    this.grid = new GridDrawer(this.canvas, this.ctx, this.tooltip, dataManager);
    this.calendarInfoModal = new CalendarInfoModal(dataManager);

    this.eventPopup = new EventPopup(this.dataManager, this.grid, this.canvasContainer);
    this.accountModal = new AccountModal(dataManager, () => this.updateAccountIcon());

    dataManager.onCalendarListChanged = () => {
      if (this.pageEl.style.display === 'none') return;
      this.renderCalendarList();
      this.updateStatistics();
    }
    dataManager.onSelectedCalendarChanged = () => {
      if (this.pageEl.style.display === 'none') return;
      this.drawGrid();
    }
    dataManager.onTogglePanel = (expand: boolean) => {
        this.togglePanel(expand);
    }

    // Sliding panel handlers
    this.panelToggle.addEventListener('click', () => {
      this.togglePanel();
    });

    // Account button handler
    this.accountBtn.addEventListener('click', () => {
      this.accountModal.show();
    });

    // Add calendar button handler
    this.addCalendarBtn.addEventListener('click', () => {
      this.calendarInfoModal.show();
    });
  }

  private updateCanvasSize(): void {
    this.canvas.width = this.canvasContainer.offsetWidth;
    this.canvas.height = this.canvasContainer.offsetHeight;
    this.drawGrid();
  }

  private updateStatistics(): void {
    const { birthDate, totalDays } = this.dataManager.getSelectedCalendarInfo();
    const today = dayjs();
    const daysLived = today.diff(birthDate, 'day');
    const daysRemaining = Math.max(0, totalDays - daysLived);

    const daysLivedRowEl = document.getElementById('daysLivedRow');
    const daysRemainingRowEl = document.getElementById('daysRemainingRow');

    if (daysLivedRowEl) {
      daysLivedRowEl.textContent = `${daysLived.toLocaleString()}`;
    }
    if (daysRemainingRowEl) {
      daysRemainingRowEl.textContent = daysRemaining.toLocaleString();
    }
  }

  private updateAccountIcon(): void {
    const accountIcon = this.accountBtn.querySelector('img') as HTMLImageElement;
    if (accountIcon) {
      if (this.dataManager.isLoggedIn()) {
        accountIcon.src = 'img/account.svg';
      } else {
        accountIcon.src = 'img/create-account.svg';
      }
    }
  }

  private togglePanel(expand?: boolean): void {
    if (expand == null) {
      expand = !this.slidingPanel.classList.contains('expanded');
    } else if (expand == this.slidingPanel.classList.contains('expanded')) {
      return
    }
    this.slidingPanel.classList.toggle('expanded');

    // Update toggle button icon
    const toggleIcon = this.panelToggle.querySelector('.toggle-icon');
    if (toggleIcon) {
      toggleIcon.textContent = expand ? '◂' : '▸';
    }

    this.dataManager.toggleCalendarPanel(expand)
    this.updateCanvasSize();
  }

  private renderCalendarList(): void {
    const calendars = this.dataManager.getCalendars();
    const selectedId = this.dataManager.getSelectedCalendarId();
    
    this.calendarList.innerHTML = '';
    
    calendars.forEach((calendar, _index) => {
      const calendarItem = document.createElement('div');
      calendarItem.className = 'calendar-item';
      if (calendar.id === selectedId) {
        calendarItem.classList.add('selected');
      }
      
      const title = escapeHtml(calendar.title) || '\u200B';
      const isLast = calendars.length == 1
      
      calendarItem.innerHTML = `
        <div class="calendar-item-content">
          <div class="calendar-item-title">${title}</div>
          <div class="calendar-item-buttons">
            <button class="calendar-item-btn edit-btn" title="Edit Calendar">✏️</button>
            <button class="calendar-item-btn copy-btn" title="Clone Calendar">📄</button>
            <button class="calendar-item-btn delete-btn" title="Delete Calendar"${isLast ? ' style=\"visibility: hidden\"' : ''}>🗑️</button>
          </div>
        </div>
      `;
      
      calendarItem.addEventListener('click', (_) => {
        this.selectCalendar(calendar.id);
      });
      
      const editBtn = calendarItem.querySelector('.edit-btn') as HTMLButtonElement;
      editBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.editCalendar(calendar.id);
      });

      const copyBtn = calendarItem.querySelector('.copy-btn') as HTMLButtonElement;
      copyBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.copyCalendar(calendar.id);
      });
      
      const deleteBtn = calendarItem.querySelector('.delete-btn') as HTMLButtonElement;
      deleteBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        await this.deleteCalendar(calendar.id);
      });
      
      this.calendarList.appendChild(calendarItem);
    });
  }

  private selectCalendar(id: string): void {
    this.eventPopup.close();
    this.dataManager.setSelectedCalendarId(id);
  }

  private editCalendar(id: string): void {
    this.calendarInfoModal.showForEdit(id);
  }

  private copyCalendar(id: string): void {
    this.calendarInfoModal.showForCopy(id);
  }

  private async deleteCalendar(id: string): Promise<void> {
    const calendars = this.dataManager.getCalendars();
    if (calendars.length <= 1) {
      showWarning('Cannot delete the last calendar');
      return;
    }
    
    const calendar = calendars.find(c => c.id === id);
    if (!calendar) {
      showError('Calendar not found');
      return;
    }
    const title = calendar.title || `Calendar`;
    
    if (confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      this.dataManager.deleteCalendar(id);
    }
  }

  public drawGrid() {
    this.grid.drawGrid()
  }

  public show() {
    this.pageEl.style.display = 'flex';
    this.canvas.width = this.canvasContainer.offsetWidth;
    this.canvas.height = this.canvasContainer.offsetHeight;
    this.togglePanel(this.dataManager.isCalendarPanelOpen())
    this.renderCalendarList();
    this.updateStatistics();
    this.drawGrid();
    this.updateAccountIcon();
  }

  public hide() {
    this.pageEl.style.display = 'none';
  }

  public onResize() {
    if (this.pageEl.style.display !== 'none') {
      this.updateCanvasSize();
    }
  }

  setupHandlers() {
    this.canvas.addEventListener('mousemove', (e) => {
      if (this.pageEl.style.display === 'none') return;
      if (this.eventPopup.isOpen()) return;
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      this.grid.highlightCell(mouseX, mouseY);
    });

    this.canvas.addEventListener('mouseout', () => {
      if (this.pageEl.style.display === 'none') return;
      if (this.eventPopup.isOpen()) return;
      this.grid.highlightCell(-1, -1);
    });

    this.canvas.addEventListener('mousedown', (e) => {
      if (this.pageEl.style.display === 'none') return;
      if (this.eventPopup.isOpen()) {
        this.eventPopup.close();
        return;
      }
      const rect = this.canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      this.grid.startSelection(clickX, clickY);
    });

    const colorInput = document.getElementById('colorPicker') as HTMLInputElement;
    colorInput.value = DEFAULT_EVENT_COLOR

    this.canvas.addEventListener('mouseup', (e) => {
      if (this.pageEl.style.display === 'none') return;
      if (this.eventPopup.isOpen()) {
        this.eventPopup.close();
        return;
      }

      let calendar = this.dataManager.getSelectedCalendarInfo();

      let [index, length] = this.grid.getSelection();
      if (index < 0 || index >= calendar.totalDays) return;

      const existingEvent = this.dataManager.getEventForDay(index);
      if (existingEvent && length == 1) {
        index = existingEvent.index;
        length = existingEvent.length;
      }

      this.eventPopup.show(calendar, index, length, existingEvent, e.clientX, e.clientY);
    });
  }
} 